import { useMemo } from 'react';
import type { BuildState, BuildDerived } from '@/types';
import { ANIMALS, evaluateAnimal, type Animal } from '@/data/animals';

/**
 * Kill-confidence verdict — the emotional centerpiece of the build.
 *
 * For each iconic game animal, walks the build's downrange KE + momentum (from
 * the same BC trajectory model the rest of Build Review uses) against that
 * species' published ethical threshold (P&Y / SCI, in animals.ts) and reports
 * the MAX RANGE at which the shot is still ethically lethal. This answers the
 * one question a bowhunter actually cares about — "will it kill, and how far" —
 * in plain language, backed by sourced thresholds. Nobody else's tool does this.
 */

const GRAINS_PER_SLUG = 7000 * 32.174;
const KE_DIVISOR = 450_240; // grains·fps² → ft·lb
// Iconic NA spread across the lethality range: easy → western icon → heavy test.
const ICONIC = ['Whitetail deer', 'Elk', 'Moose (Shiras)'];
const RANGES = Array.from({ length: 21 }, (_, i) => i * 5); // 0..100 yd
// Velocity retained per yard, anchored to the calibrated wind-drift engine
// (~89% velocity retained at 75 yd for a typical hunting arrow). Exponential
// decay from that anchor — far more realistic than the old BC drop model.
const RETAIN_PER_YARD = Math.pow(0.89, 1 / 75); // ≈ 0.99845

interface KillVerdictProps {
  state: BuildState;
  derived: BuildDerived;
}

/** Shared kill-range computation — used by the verdict card AND the share card. */
export interface IconicKill {
  name: string;
  /** Furthest 'pass' yardage (capped at 100; >=100 means "100+"). */
  ethicalTo: number;
}
export function iconicKillRanges(muzzleFps: number, weightGrains: number): IconicKill[] {
  const massSlug = weightGrains / GRAINS_PER_SLUG;
  const pts = RANGES.map((yd) => {
    const v = muzzleFps * Math.pow(RETAIN_PER_YARD, yd);
    return { yd, ke: (weightGrains * v * v) / KE_DIVISOR, mom: massSlug * v };
  });
  return ICONIC.map((name) => {
    const animal = ANIMALS.find((a) => a.name === name)!;
    let ethicalTo = 0;
    for (const pt of pts) {
      if (evaluateAnimal(animal, pt.ke, pt.mom).tier === 'pass') ethicalTo = pt.yd;
    }
    return { name, ethicalTo };
  });
}

interface RangeVerdict {
  animal: Animal;
  /** Furthest yardage still 'pass' (clean ethical kill). 0 = never. */
  ethicalTo: number;
  /** Furthest yardage still legal but marginal ('warn'). */
  marginalTo: number;
}

export function KillVerdict({ state, derived }: KillVerdictProps) {
  const verdicts = useMemo<RangeVerdict[]>(() => {
    const muzzle = state.fps;
    const massSlug = derived.weight / GRAINS_PER_SLUG;
    const pts = RANGES.map((yd) => {
      const v = muzzle * Math.pow(RETAIN_PER_YARD, yd);
      const ke = (derived.weight * v * v) / KE_DIVISOR;
      return { yd, ke, mom: massSlug * v };
    });

    return ICONIC.map((name) => {
      const animal = ANIMALS.find((a) => a.name === name)!;
      let ethicalTo = 0;
      let marginalTo = 0;
      for (const pt of pts) {
        const tier = evaluateAnimal(animal, pt.ke, pt.mom).tier;
        if (tier === 'pass') ethicalTo = pt.yd;
        if (tier !== 'fail') marginalTo = pt.yd;
      }
      return { animal, ethicalTo, marginalTo };
    });
  }, [state.fps, derived.weight]);

  return (
    <div
      style={{
        border: '1px solid #3f3f46',
        borderRadius: 10,
        overflow: 'hidden',
        background: 'linear-gradient(180deg,#141416,#0a0a0b)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: '1px solid #27272a',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, letterSpacing: '0.04em', color: '#fafafa' }}>
          WILL IT KILL?
        </h2>
        <span style={{ fontSize: 11, fontFamily: 'ui-monospace, monospace', color: '#71717a' }}>
          ethical max range · {Math.round(derived.ke)} ft·lb at launch
        </span>
      </div>

      <div style={{ padding: '6px 8px' }}>
        {verdicts.map((v) => (
          <VerdictRow key={v.animal.name} v={v} />
        ))}
      </div>

      <div style={{ padding: '8px 16px 12px', fontSize: 10.5, color: '#52525b', lineHeight: 1.4 }}>
        Ethical-shot thresholds from Pope &amp; Young / SCI penetration data — clean broadside,
        sharp broadhead assumed. Your judgment in the field is the final authority.
      </div>
    </div>
  );
}

function VerdictRow({ v }: { v: RangeVerdict }) {
  const { animal, ethicalTo, marginalTo } = v;
  const tier = ethicalTo >= 40 ? 'good' : ethicalTo > 0 ? 'close' : 'no';
  const color = tier === 'good' ? '#22c55e' : tier === 'close' ? '#f59e0b' : '#ef4444';
  const icon = tier === 'no' ? '✕' : '✓';
  const capped = ethicalTo >= 100;

  const line =
    tier === 'no'
      ? 'not enough penetration at any range'
      : capped
        ? 'penetration holds well past any ethical bow range'
        : tier === 'close'
          ? `ethical to ${ethicalTo} yd — keep it close`
          : `ethically lethal to ${ethicalTo} yd`;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 12px',
        borderRadius: 7,
      }}
    >
      <span style={{ color, fontSize: 18, fontWeight: 700, width: 16, textAlign: 'center' }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#e4e4e7' }}>{animal.name}</div>
        <div style={{ fontSize: 12, color }}>{line}</div>
      </div>
      {ethicalTo > 0 && (
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1 }}>{capped ? '100+' : ethicalTo}</div>
          <div style={{ fontSize: 9, fontFamily: 'ui-monospace, monospace', color: '#71717a', letterSpacing: '0.08em' }}>
            YARDS
            {!capped && marginalTo > ethicalTo ? ` · ~${marginalTo} max` : ''}
          </div>
        </div>
      )}
    </div>
  );
}
